import { ChangeEvent, KeyboardEvent, useState } from 'react';
import { Button, Form, FormListOperation, Input } from 'antd';
import { FormListFieldData } from 'antd/lib/form/FormList';

import { RemoveIcon } from '@pages/risk/variables';
import cls from './VariablesAnswer.module.css';

interface VariablesAnswerProps {
  fields: FormListFieldData[];
  operation: FormListOperation;
  onChange: (answerIndex: number, value: string) => void;
  readonly?: boolean;
}

export const VariablesAnswer = ({
  fields, operation, onChange, readonly,
}: VariablesAnswerProps) => {
  const [percentValue, setPercentValue] = useState('0');

  const normalizePercentValue = (rawValue: string): string => {
    const sanitizedValue = rawValue
      .replace(',', '.')
      .replace(/[^\d.]/g, '');
    const hasDecimalSeparator = sanitizedValue.includes('.');

    const [integerPart = '', decimalPart = ''] = sanitizedValue.split('.');
    const limitedDecimalPart = decimalPart.slice(0, 2);
    const integerValue = integerPart === '' ? null : Number(integerPart);

    if (integerValue !== null && integerValue > 100) {
      return '100';
    }

    if (integerValue === 100 && hasDecimalSeparator) {
      return '100.00';
    }

    const normalizedValue = hasDecimalSeparator
      ? `${integerPart}.${limitedDecimalPart}`
      : integerPart;
    const parsedValue = Number(normalizedValue);

    if (Number.isNaN(parsedValue)) {
      return '';
    }

    return parsedValue > 100 ? '100' : normalizedValue;
  };

  const onAnswerPercentChange = (
    e: ChangeEvent<HTMLInputElement>, answerIndex: number,
  ) => {
    const inputValue = e.target.value;
    const isValidInput = /^(\d{0,3}([.,]\d{0,2})?)?$/.test(inputValue);
    if (!isValidInput) {
      return;
    }

    const value = normalizePercentValue(inputValue);
    setPercentValue(value);
    onChange(answerIndex, value);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!/[0-9.,]/.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className={cls._}>
      {fields.map((field) => {
        return (
          <div key={field.key} className={cls.option}>
            {!readonly ? (
              <div className={cls.remove} onClick={() => void operation.remove(field.name)}>
                <RemoveIcon />
              </div>
            ) : null}
            <Form.Item noStyle name={[field.name, 'title']} className={cls.title}>
              <Input
                className={cls.title}
                placeholder="Variable title"
                maxLength={100}
                onBeforeInputCapture={readonly ? e => e.preventDefault() : undefined}
                disabled={readonly}
              />
            </Form.Item>
            <Form.Item name={[field.name, 'progressPercent']} className={cls.percent}>
              <Input
                placeholder="0%"
                required
                onChange={(e) => onAnswerPercentChange(e, field.name + 1)}
                value={+percentValue > 100 ? '100' : percentValue}
                maxLength={6}
                onKeyPress={handleKeyPress}
                onBeforeInputCapture={readonly ? e => e.preventDefault() : undefined}
                disabled={readonly}
              />
            </Form.Item>

            <Form.Item name={[field.name, 'recommendations']} className={cls.recommendation}>
              <Input
                placeholder="Empty"
                required
                onChange={(e) => onAnswerPercentChange(e, field.name + 1)}
                disabled={readonly}
              />
            </Form.Item>
          </div>
        );
      })}

      <div className={cls.action}>
        <Button type="default" disabled={readonly} onClick={() => operation.add()}
          block className={cls.add}>
          <RemoveIcon />
          Option {fields.length + 1}
        </Button>
      </div>
    </div>
  );
};
